/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/auth";
import { parseQuery } from "../../utility";
import BorderLoader from "../../components/atoms/loader/BorderLoader";
import { useSelector, useDispatch } from "react-redux";
import type { Store } from "../../store";
import { modalActions } from "../../modules/modal";
import { push } from "connected-react-router";
import { authActions } from "modules/auth";
import {
  createViewerAppDynamicLinkWithParam,
  createDynamicLinkWithParam,
} from "utility/firebase";
import { EditForm, ContentsLayout } from "../../styles";
import { pushDataLayerWithClientBuildUnixTime } from "utility/ga";

type Props = Record<string, never>;

const OpenId: React.FC<Props> = (_props) => {
  const dispatch = useDispatch();
  const location = useSelector((state: Store) => state.router.location.search);
  const query = parseQuery(location);

  const didMount = () => {
    const dictToURI = (dict: Record<string, unknown>) => {
      const str = [];
      for (const p in dict) {
        str.push(
          encodeURIComponent(p) + "=" + encodeURIComponent(dict[p] as never)
        );
      }
      return str.join("&");
    };
    const getQueryValue = (
      query: Record<string, string> | null,
      target: string
    ): string | null => {
      if (!!query && !!query[target]) {
        return query[target];
      } else {
        return null;
      }
    };
    /**
     * docomo・FANY ID とも、from にはログインを始めたページの URL を入れ、
     * そのページと同じ origin の /openid へ戻してくる。
     * origin とスキームが今のページと同じ URL だけを使い、別の origin や
     * javascript:・blob: などの URL へは遷移させず、トップへ置き換える
     */
    const toSameOriginFromUrl = (from: string) => {
      const topUrl = `${window.location.origin}/`;
      try {
        const fromUrl = new URL(from, window.location.origin);
        return fromUrl.origin === window.location.origin &&
          fromUrl.protocol === window.location.protocol
          ? fromUrl.href
          : topUrl;
      } catch {
        return topUrl;
      }
    };
    const getRedirectUrl = (from: string, params: Record<string, unknown>) => {
      const queryStr = dictToURI(params);
      const fromUrl = toSameOriginFromUrl(from);
      return fromUrl + (fromUrl.includes("?") ? "&" : "?") + queryStr;
    };

    const sendDocomoSignupEventToGA = (isNewUser: boolean) => {
      if (isNewUser) {
        pushDataLayerWithClientBuildUnixTime({
          event: "sign_up",
          method: "Docomo",
        });
      } else {
        pushDataLayerWithClientBuildUnixTime({
          event: "login",
          method: "Docomo",
        });
      }
    };

    const sendFanyIdSignInEventToGA = (isNewUser: boolean) => {
      if (isNewUser) {
        pushDataLayerWithClientBuildUnixTime({
          event: "sign_up",
          method: "FanyId",
        });
      } else {
        pushDataLayerWithClientBuildUnixTime({
          event: "login",
          method: "FanyId",
        });
      }
    };

    const sendSignupEventToGA = (
      isNewUser: boolean,
      provider: string | null
    ) => {
      switch (provider) {
        case "fanyid":
          sendFanyIdSignInEventToGA(isNewUser);
          break;
        default:
          // 元々DocomoのみGAに送信していたが、custom tokenを利用するログイン方法にFANY IDが追加された
          // queryパラメータ:providerはdocomoログイン時は送信されないため値が無いような場合の考慮としてdefaultでdocomo判定にしている
          sendDocomoSignupEventToGA(isNewUser);
      }
    };

    const tokenLogin = async (
      from: string | null,
      token: string | null,
      params: Record<string, unknown>,
      isSignUp: boolean,
      provider: string | null
    ) => {
      try {
        if (token !== null) {
          await firebase.auth().signInWithCustomToken(token);
          sendSignupEventToGA(isSignUp, provider);
        }
        if (from === null) {
          if (msg !== null) {
            dispatch(modalActions.toggleError({ msg }));
          } else if (errorCode !== null) {
            dispatch(
              modalActions.toggleError({
                msg: `エラーにより処理できませんでした．ErrorCode: ${errorCode}`,
              })
            );
          }
        } else {
          const url = getRedirectUrl(from, params);
          window.location.href = url;
        }
      } catch (e) {
        console.error(e);
      }
    };

    const msg = getQueryValue(query, "msg");
    const errorCode = getQueryValue(query, "errorCode");
    const token = getQueryValue(query, "ctoken");
    const isSignUp = getQueryValue(query, "isSignUp");
    const from = getQueryValue(query, "from");
    const appName = getQueryValue(query, "appName");
    const provider = getQueryValue(query, "provider");
    // if viewer app login,
    if (appName === "viewerApp") {
      dispatch(
        authActions.createAppDynamicLink({
          // @ts-expect-error TS2322
          ar: null,
          // @ts-expect-error TS2322
          viewer: createViewerAppDynamicLinkWithParam("docomo", { token }),
        })
      );
      dispatch(push(`/viewer-app-login`));
      return;
    } else if (appName === "arApp") {
      dispatch(
        authActions.createAppDynamicLink({
          // @ts-expect-error TS2322
          ar: createDynamicLinkWithParam("docomo", { token }),
          // @ts-expect-error TS2322
          viewer: null,
        })
      );
      dispatch(push(`/ar-app-login-ehgeywoyhoebavro`));
      return;
    }
    let params = {};
    if (msg !== null) {
      params = { msg, ...params };
    }
    if (errorCode !== null) {
      params = { errorCode, ...params };
    }
    tokenLogin(from, token, params, isSignUp === "True", provider);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(didMount, []);

  return (
    <div id="contents">
      <ContentsLayout
        css={css`
          min-height: calc(100vh - 601px);
          padding: 40px;
          @media screen and (min-width: 768px) {
            min-height: calc(100vh - 386px);
          }
        `}
      >
        <div className="prifle_edit_info">
          <EditForm>
            <BorderLoader />
          </EditForm>
        </div>
      </ContentsLayout>
    </div>
  );
};

export default OpenId;
